export * from '@nike/aegis-auth';
import React, { useCallback, useEffect } from 'react';
import create from 'zustand';

const useAuthStore = create((set, get) => ({
    hasInitStarted: false,
    loginComplete: false,
    accessToken: null,
    idToken: null,
    tokenClaims: null,
    idClaims: null,
    scopes: [],
    loginError: null,
    isLoggedIn: () => !!get().accessToken,
    setAccessToken: (token) => {
        var _a, _b, _c;
        return set({
            loginError: null,
            loginComplete: true,
            accessToken: (_a = token === null || token === void 0 ? void 0 : token.accessToken) !== null && _a !== void 0 ? _a : null,
            tokenClaims: (_b = token === null || token === void 0 ? void 0 : token.claims) !== null && _b !== void 0 ? _b : null,
            scopes: (_c = token === null || token === void 0 ? void 0 : token.scopes) !== null && _c !== void 0 ? _c : [],
        });
    },
    setIdToken: (token) => {
        var _a, _b;
        return set({
            idToken: (_a = token === null || token === void 0 ? void 0 : token.idToken) !== null && _a !== void 0 ? _a : null,
            idClaims: (_b = token === null || token === void 0 ? void 0 : token.claims) !== null && _b !== void 0 ? _b : null,
        });
    },
    setLoginError: (error) => set({
        loginError: error,
        loginComplete: true,
        accessToken: null,
        tokenClaims: null,
        scopes: [],
    }),
    client: null,
    clearLogin: () => {
        set({
            loginError: null,
            loginComplete: true,
            accessToken: null,
            idToken: null,
            tokenClaims: null,
            idClaims: null,
            scopes: [],
        });
    },
    startInit: () => set({ hasInitStarted: true }),
    setClient: (client) => set({ client }),
}));
function selectLoginComplete(state) {
    return state.loginComplete;
}
// TODO: remove this badly named function in vNext
function selectisLoggedIn(state) {
    return state && state.isLoggedIn && state.isLoggedIn();
}
function selectIsLoggedIn(state) {
    return state && state.isLoggedIn && state.isLoggedIn();
}
function selectUsername(state) {
    var _a, _b;
    return (_b = (_a = state.tokenClaims) === null || _a === void 0 ? void 0 : _a.sub) !== null && _b !== void 0 ? _b : null;
}
function selectAccessToken(state) {
    return state.accessToken;
}
function selectIdToken(state) {
    return state.idToken;
}
function selectLoginError(state) {
    return state.loginError;
}
function selectClient(state) {
    return state.client;
}
function selectIdName(state) {
    var _a, _b;
    return (_b = (_a = state.idClaims) === null || _a === void 0 ? void 0 : _a.name) !== null && _b !== void 0 ? _b : null;
}
function selectEmail(state) {
    var _a, _b;
    return (_b = (_a = state.idClaims) === null || _a === void 0 ? void 0 : _a.email) !== null && _b !== void 0 ? _b : null;
}

function useAuth(client) {
    const store = useAuthStore();
    const { setClient, startInit, setAccessToken, setIdToken, setLoginError, hasInitStarted, clearLogin, client: storeClient, } = store;
    // The initial render needs the client for the useAuthEvent registrations
    // This is a state-hack
    if (!storeClient) {
        setClient(client);
    }
    const handleLogin = useCallback(({ accessToken, idToken }) => {
        setAccessToken(accessToken);
        setIdToken(idToken);
    }, [setAccessToken]);
    const handleRenew = useCallback((accessToken) => {
        setAccessToken(accessToken);
    }, [setAccessToken]);
    useAuthEvent('login:success', handleLogin);
    useAuthEvent('renew:access:success', handleRenew);
    useAuthEvent('login:error', setLoginError);
    useAuthEvent('renew:error', setLoginError);
    useAuthEvent('logout:success', () => {
        clearLogin();
    });
    useEffect(() => {
        if (!hasInitStarted) {
            startInit();
            client.init({ originalUri: window.location.href });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    return store;
}
function useAuthEvent(event, handler) {
    const client = selectClient(useAuthStore.getState());
    useEffect(() => {
        if (!client)
            return;
        client.on(event, handler);
        return () => client.off(event, handler);
    }, [client, handler]);
}

const DefaultButton = (props) => React.createElement("button", Object.assign({}, props));
function LoginRequired({ client, children, Button = DefaultButton }) {
    const { loginComplete, isLoggedIn } = useAuth(client);
    if (!loginComplete) {
        return null;
    }
    if (!isLoggedIn) {
        return (React.createElement("div", { style: { margin: '1rem' } },
            React.createElement("p", null, "You must be logged in to view this page"),
            React.createElement(Button, { onClick: client.login }, "Login")));
    }
    return React.Children.only(children);
}

export { LoginRequired, selectAccessToken, selectClient, selectEmail, selectIdName, selectIdToken, selectIsLoggedIn, selectLoginComplete, selectLoginError, selectUsername, selectisLoggedIn, useAuth, useAuthEvent, useAuthStore };
